import { z } from 'zod';
import { countryCodeSchema } from './countries';
import { SCRIPT_LENGTHS } from './products/script';
import { MAX_EXTRACTED_CITIES, scriptCitySchema, timePeriodSchema } from './script-project';

/** A band ends below this many words: short under 8,000, medium under 20,000, long under 50,000. */
export const SCRIPT_LENGTH_LIMITS = { short: 8000, medium: 20000, long: 50000 } as const;

export type ScriptLengthBand = (typeof SCRIPT_LENGTHS)[number] | 'too_long' | 'unreadable';

/**
 * The length band of a script from its word count. No readable words is `unreadable` and 50,000 or more is
 * `too_long`: neither can be priced automatically. Mirrors `script_length_for` in SQL, which is what decides.
 */
export function scriptLengthFor(wordCount: number): ScriptLengthBand {
  if (wordCount <= 0) return 'unreadable';
  if (wordCount < SCRIPT_LENGTH_LIMITS.short) return 'short';
  if (wordCount < SCRIPT_LENGTH_LIMITS.medium) return 'medium';
  if (wordCount < SCRIPT_LENGTH_LIMITS.long) return 'long';
  return 'too_long';
}

/**
 * What is read from a script to fill the order form in. Every list is closed (periods, ISO countries), and cities
 * appear only when one to four are clearly the setting, each with its country.
 */
export const scriptExtractionSchema = z.object({
  title: z.string().max(200).optional(),
  episodeTitle: z.string().max(200).optional(),
  episodeNumber: z.string().max(50).optional(),
  authors: z.array(z.string().min(1).max(200)).max(20),
  producer: z.string().max(200).optional(),
  timePeriods: z.array(timePeriodSchema),
  countries: z.array(countryCodeSchema),
  cities: z.array(scriptCitySchema).max(MAX_EXTRACTED_CITIES),
});
export type ScriptExtraction = z.infer<typeof scriptExtractionSchema>;

export const SCRIPT_ANALYSIS_STATUSES = ['measuring', 'ready', 'unreadable', 'too_long'] as const;
export const SCRIPT_EXTRACTION_STATUSES = [
  'pending',
  'done',
  'timed_out',
  'failed',
  'skipped',
] as const;
export type ScriptExtractionStatus = (typeof SCRIPT_EXTRACTION_STATUSES)[number];

const timestamp = z.iso.datetime({ offset: true });

/**
 * A row of `public.script_analyses`: what was measured and read from a project's script. Written by the worker's
 * service role only, because the word count sets the price; read by whoever may read the project.
 */
export const scriptAnalysisSchema = z.object({
  id: z.uuid(),
  project_id: z.uuid(),
  file_id: z.uuid(),
  status: z.enum(SCRIPT_ANALYSIS_STATUSES),
  word_count: z.number().int().nonnegative().nullable(),
  page_count: z.number().int().nonnegative().nullable(),
  length: z.enum(SCRIPT_LENGTHS).nullable(),
  extraction: z.record(z.string(), z.unknown()).nullable(),
  extraction_status: z.enum(SCRIPT_EXTRACTION_STATUSES),
  created_at: timestamp,
  completed_at: timestamp.nullable(),
});
export type ScriptAnalysis = z.infer<typeof scriptAnalysisSchema>;

/** `POST /api/projects/:projectId/script`: the object the caller has just uploaded, and what the file was called. */
export const scriptUploadRequestSchema = z.strictObject({
  fileId: z.uuid(),
  filename: z.string().trim().min(1).max(255),
});
export type ScriptUploadRequest = z.infer<typeof scriptUploadRequestSchema>;

/** The answer: the finished analysis (measured, and the fields read or given up on). */
export const scriptUploadResponseSchema = z.object({ analysis: scriptAnalysisSchema });
export type ScriptUploadResponse = z.infer<typeof scriptUploadResponseSchema>;

/** Why a script was not accepted; the worker answers 422 with one of these and nothing of the script is kept. */
export const SCRIPT_REFUSAL_CODES = [
  'script_unreadable',
  'script_too_long',
  'not_a_pdf',
  'file_too_large',
] as const;
export type ScriptRefusalCode = (typeof SCRIPT_REFUSAL_CODES)[number];
